import { AutoFocusModule } from 'primeng/autofocus';
import { Component, signal } from '@angular/core';
import { FieldBase } from './field-base';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ReactiveFormsModule } from '@angular/forms';
import { SafeHtmlPipe } from '../../pipe/safe-html.pipe';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'field-password',
    imports: [
        AutoFocusModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        ReactiveFormsModule,
        SafeHtmlPipe,
        TooltipModule
    ],
    template: `
        <div [formGroup]="form()">
            <label
                class="text-base ellipsis-label {{labelClass}}"
                for="{{key}}"
                pTooltip="{{label}}"
                [fitContent]="false"
            >
                {{label}}
                @if ( required ) {
                    <span class="text-red-600">*</span>
                }
            </label>

            <div class="relative w-full">
                @if ( icon ) {
                    <p-iconField class="w-full">
                        <p-inputIcon styleClass="{{icon}}" />
                        <input
                            #field
                            [pAutoFocus]="setFocus()"
                            (keydown.enter)="onNext($event)"
                            id="{{key}}"
                            class="password-input text-base text-color surface-overlay p-2 pl-6 border-1 border-solid surface-border focus:border-primary w-full"
                            formControlName="{{key}}"
                            pInputText
                            [type]="showPassword() ? 'text' : 'password'"
                            [attr.aria-describedby]="isInvalid || fieldError.trim().length > 0 ? key + '-error' : null"
                            [attr.aria-invalid]="isInvalid || fieldError.trim().length > 0"
                            [attr.autocomplete]="autocomplete || 'new-password'"
                            [attr.placeholder]="placeholder || null"
                            [attr.maxlength]="maxLengthAttr"
                            showDelay="500"
                            pTooltip="{{tip || label}}"
                            tooltipPosition="bottom"
                            [fitContent]="false"/>
                    </p-iconField>
                } @else {
                    <input
                        #field
                        [pAutoFocus]="setFocus()"
                        (keydown.enter)="onNext($event)"
                        id="{{key}}"
                        class="password-input text-base text-color surface-overlay p-2 border-1 border-solid surface-border focus:border-primary w-full"
                        formControlName="{{key}}"
                        pInputText
                        [type]="showPassword() ? 'text' : 'password'"
                        [attr.aria-describedby]="isInvalid || fieldError.trim().length > 0 ? key + '-error' : null"
                        [attr.aria-invalid]="isInvalid || fieldError.trim().length > 0"
                        [attr.autocomplete]="autocomplete || 'new-password'"
                        [attr.placeholder]="placeholder || null"
                        [attr.maxlength]="maxLengthAttr"
                        showDelay="500"
                        pTooltip="{{tip || label}}"
                        tooltipPosition="bottom"
                        [fitContent]="false"/>
                }

                <button
                    class="password-visibility text-500"
                    [class.cursor-not-allowed]="isControlDisabled"
                    type="button"
                    [disabled]="isControlDisabled"
                    [attr.aria-label]="showPassword() ? 'Ocultar senha' : 'Mostrar senha'"
                    (click)="togglePasswordVisibility()"
                >
                    <i class="pi {{ showPassword() ? 'pi-eye-slash' : 'pi-eye' }}"></i>
                </button>
            </div>

            <small id="{{key}}-error" class="field-feedback text-red-600 block mt-1" aria-live="polite" [innerHtml]="fieldError | safeHtml"></small>
        </div>
    `,
    styles: `
        .password-visibility {
            align-items: center;
            background: transparent;
            border: 0;
            display: inline-flex;
            justify-content: center;
            min-height: 44px;
            min-width: 44px;
            padding: 0;
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
        }

        .password-input {
            padding-inline-end: 52px !important;
        }

        .password-visibility:not(:disabled) {
            cursor: pointer;
        }

        .password-visibility:focus-visible {
            border-radius: 50%;
            outline: 2px solid var(--p-primary-500);
            outline-offset: -4px;
        }

        .field-feedback {
            min-height: 1rem;
        }
    `
})
export class FieldPassword extends FieldBase<string> {
    override type!: string
    showPassword = signal(false);

    constructor(){
        super()
        this.controlType = 'textbox'
    }

    get isControlDisabled(): boolean {
        return this.form().get(this.key)?.disabled ?? false;
    }

    togglePasswordVisibility(): void {
        if (this.isControlDisabled) return;
        this.showPassword.update(visible => !visible);
    }
}
